import { useState, useRef, useCallback, useEffect } from "react";
import { api } from "../lib/api";

export interface StreamMetrics {
  startTime: number | null;
  firstByteTime: number | null;
  ttfbMs: number | null;
  totalChunks: number;
  totalChars: number;
  charsPerSecond: number;
  durationMs: number;
}

export interface UseAIStreamOptions {
  /** SSE streaming endpoint (default: '/api/chat/stream') */
  endpoint?: string;
  /** Non-streaming fallback endpoint (default: '/api/chat') */
  fallbackEndpoint?: string;
  /** Custom headers to send with fetch */
  headers?: Record<string, string>;
  /** Default body payload */
  defaultBody?: Record<string, any>;
  /** Maximum auto-reconnection attempts (default: 3) */
  maxRetries?: number;
  /** Delay in ms between retries, scaled exponentially (default: 1000) */
  retryDelayMs?: number;
  /** Whether auto-reconnection is enabled (default: true) */
  autoReconnect?: boolean;
  /** Callback fired whenever a new stream chunk arrives */
  onChunk?: (chunk: string, accumulated: string) => void;
  /** Callback fired when stream completes successfully */
  onComplete?: (finalText: string) => void;
  /** Callback fired on stream or network error */
  onError?: (error: Error, partialText: string) => void;
  /** Callback fired when stream attempts reconnection */
  onReconnect?: (attempt: number, maxRetries: number) => void;
}

export interface UseAIStreamReturn {
  /** Accumulated response text (supports seamless markdown/code) */
  completion: string;
  /** True when stream or fallback is in-flight */
  isLoading: boolean;
  /** True specifically when actively consuming SSE ReadableStream */
  isStreaming: boolean;
  /** Error instance if request failed */
  error: Error | null;
  /** Current reconnection attempt number */
  reconnectCount: number;
  /** True if stream was manually aborted */
  isAborted: boolean;
  /** True if stream degraded to non-streaming fallback */
  isDegraded: boolean;
  /** Real-time performance & latency metrics */
  metrics: StreamMetrics;
  /** Trigger AI streaming generation */
  startStream: (
    promptOrMessages: string | Array<{ role: string; content: string }>,
    customOptions?: { body?: Record<string, any>; headers?: Record<string, string> }
  ) => Promise<string>;
  /** Immediately abort active stream (preserves partial completion) */
  abortStream: () => void;
  /** Reset completion, errors, and metrics */
  reset: () => void;
  /** Manually trigger fallback non-streaming fetch */
  fallbackSyncFetch: (
    promptOrMessages: string | Array<{ role: string; content: string }>
  ) => Promise<string>;
}

const INITIAL_METRICS: StreamMetrics = {
  startTime: null,
  firstByteTime: null,
  ttfbMs: null,
  totalChunks: 0,
  totalChars: 0,
  charsPerSecond: 0,
  durationMs: 0,
};

/**
 * Robust React Hook for consuming Server-Sent Events (SSE) ReadableStreams
 * from Cloudflare Workers AI with auto-reconnection, markdown chunk accumulation,
 * error boundary fallbacks, and performance metrics.
 */
export function useAIStream(options: UseAIStreamOptions = {}): UseAIStreamReturn {
  const {
    endpoint = "/api/chat/stream",
    fallbackEndpoint = "/api/chat",
    headers: defaultHeaders = {},
    defaultBody = {},
    maxRetries = 3,
    retryDelayMs = 1000,
    autoReconnect = true,
    onChunk,
    onComplete,
    onError,
    onReconnect,
  } = options;

  const [completion, setCompletion] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [reconnectCount, setReconnectCount] = useState<number>(0);
  const [isAborted, setIsAborted] = useState<boolean>(false);
  const [isDegraded, setIsDegraded] = useState<boolean>(false);
  const [metrics, setMetrics] = useState<StreamMetrics>(INITIAL_METRICS);

  const abortControllerRef = useRef<AbortController | null>(null);
  const accumulatedTextRef = useRef<string>("");
  const metricsRef = useRef<StreamMetrics>(INITIAL_METRICS);

  // Clean up any ongoing stream on component unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    accumulatedTextRef.current = "";
    setCompletion("");
    setIsLoading(false);
    setIsStreaming(false);
    setError(null);
    setReconnectCount(0);
    setIsAborted(false);
    setIsDegraded(false);
    metricsRef.current = { ...INITIAL_METRICS };
    setMetrics({ ...INITIAL_METRICS });
  }, []);

  const abortStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setIsLoading(false);
    setIsAborted(true);
  }, []);

  /**
   * Fallback synchronous fetch if SSE streaming is unavailable or fails completely
   */
  const fallbackSyncFetch = useCallback(
    async (
      promptOrMessages: string | Array<{ role: string; content: string }>
    ): Promise<string> => {
      setIsLoading(true);
      setIsStreaming(false);
      setIsDegraded(true);
      setError(null);

      const messagesPayload = typeof promptOrMessages === "string"
        ? [{ role: "user", content: promptOrMessages }]
        : promptOrMessages;

      try {
        const res = await api.api.chat.$post({
          json: {
            messages: messagesPayload,
            ...defaultBody
          }
        });

        if (!res.ok) {
          throw new Error(`Fallback HTTP request failed with status ${res.status}`);
        }

        const data = await res.json() as any;
        const text = data.text || data.response || data.message || "";
        accumulatedTextRef.current = text;
        setCompletion(text);
        setIsLoading(false);
        if (onComplete) onComplete(text);
        return text;
      } catch (err: any) {
        const errorObj = err instanceof Error ? err : new Error(String(err));
        setError(errorObj);
        setIsLoading(false);
        if (onError) onError(errorObj, accumulatedTextRef.current);
        throw errorObj;
      }
    },
    [fallbackEndpoint, defaultHeaders, defaultBody, onComplete, onError]
  );

  /**
   * Main AI streaming trigger using fetch() + ReadableStreamDefaultReader
   */
  const startStream = useCallback(
    async (
      promptOrMessages: string | Array<{ role: string; content: string }>,
      customOptions?: { body?: Record<string, any>; headers?: Record<string, string> }
    ): Promise<string> => {
      // Abort previous stream if active
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      setIsStreaming(true);
      setError(null);
      setIsAborted(false);
      setIsDegraded(false);

      const messagesPayload = typeof promptOrMessages === "string"
        ? [{ role: "user", content: promptOrMessages }]
        : promptOrMessages;

      const startTime = performance.now();
      metricsRef.current = {
        startTime,
        firstByteTime: null,
        ttfbMs: null,
        totalChunks: 0,
        totalChars: accumulatedTextRef.current.length,
        charsPerSecond: 0,
        durationMs: 0,
      };
      setMetrics({ ...metricsRef.current });

      let currentRetry = 0;

      const executeStreamAttempt = async (resumeOffsetIndex: number): Promise<string> => {
        if (resumeOffsetIndex === 0) {
          accumulatedTextRef.current = "";
          setCompletion("");
        }

        const mergedHeaders = {
          "Content-Type": "application/json",
          ...defaultHeaders,
          ...(customOptions?.headers || {}),
        };

        const mergedBody = {
          messages: messagesPayload,
          resumeOffset: resumeOffsetIndex,
          ...defaultBody,
          ...(customOptions?.body || {}),
        };

        try {
          const response = await api.api.chat.stream.$post(
            {
              json: {
                messages: messagesPayload,
                prompt: typeof promptOrMessages === "string" ? promptOrMessages : undefined,
                resumeOffset: resumeOffsetIndex,
                simulateDrop: defaultBody?.simulateDrop === true,
                ...defaultBody,
                ...(customOptions?.body || {})
              }
            },
            {
              init: {
                signal: controller.signal,
                headers: mergedHeaders
              }
            }
          );

          if (!response.ok) {
            throw new Error(`Cloudflare Workers AI stream failed: HTTP ${response.status} ${response.statusText}`);
          }

          if (!response.body) {
            throw new Error("Response body is not a readable stream.");
          }

          const reader: ReadableStreamDefaultReader<Uint8Array> = response.body.getReader();
          const decoder = new TextDecoder("utf-8", { fatal: false });
          let buffer = "";

          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            // Record TTFB on first byte received
            if (!metricsRef.current.firstByteTime) {
              const fbTime = performance.now();
              metricsRef.current.firstByteTime = fbTime;
              metricsRef.current.ttfbMs = Math.round(fbTime - startTime);
            }

            // Decode raw stream binary chunks
            const chunkText = decoder.decode(value, { stream: true });
            buffer += chunkText;

            // Split buffer into SSE data lines
            const lines = buffer.split("\n");
            // The last item in `lines` may be an incomplete line; hold in buffer
            buffer = lines.pop() || "";

            let newCharsReceived = 0;

            for (const line of lines) {
              const trimmedLine = line.trim();
              if (!trimmedLine || trimmedLine.startsWith(":")) {
                continue; // Skip comments / empty keep-alive pings
              }

              if (trimmedLine.startsWith("data: ")) {
                const rawData = trimmedLine.slice(6).trim();

                if (rawData === "[DONE]") {
                  // End of SSE stream signal
                  break;
                }

                let chunkContent = "";

                // Try parsing structured SSE JSON data
                try {
                  const parsed = JSON.parse(rawData);
                  if (parsed.error) {
                    throw new Error(typeof parsed.error === 'string' ? parsed.error : JSON.stringify(parsed.error));
                  }
                  chunkContent = parsed.response || parsed.text || parsed.chunk || parsed.delta || "";
                } catch (jsonErr: any) {
                  if (jsonErr?.message?.includes("SIMULATED") || jsonErr?.message?.includes("Error")) {
                    throw jsonErr;
                  }
                  // Raw text fallback
                  chunkContent = rawData;
                }

                if (chunkContent) {
                  accumulatedTextRef.current += chunkContent;
                  newCharsReceived += chunkContent.length;

                  // Trigger incremental React state update & callback
                  setCompletion(accumulatedTextRef.current);
                  if (onChunk) {
                    onChunk(chunkContent, accumulatedTextRef.current);
                  }
                }
              }
            }

            // Update real-time performance metrics
            metricsRef.current.totalChunks += 1;
            metricsRef.current.totalChars = accumulatedTextRef.current.length;
            const elapsedTimeSec = (performance.now() - startTime) / 1000;
            metricsRef.current.durationMs = Math.round(performance.now() - startTime);
            metricsRef.current.charsPerSecond = elapsedTimeSec > 0
              ? Math.round(accumulatedTextRef.current.length / elapsedTimeSec)
              : 0;

            setMetrics({ ...metricsRef.current });
          }

          setIsStreaming(false);
          setIsLoading(false);

          if (onComplete) {
            onComplete(accumulatedTextRef.current);
          }

          return accumulatedTextRef.current;
        } catch (err: any) {
          if (err.name === "AbortError" || controller.signal.aborted) {
            setIsStreaming(false);
            setIsLoading(false);
            setIsAborted(true);
            return accumulatedTextRef.current; // Preserve partial text
          }

          console.warn(`[useAIStream] Stream error (Attempt ${currentRetry + 1}/${maxRetries}):`, err?.message);

          // Auto-reconnection attempt logic
          if (autoReconnect && currentRetry < maxRetries && !controller.signal.aborted) {
            currentRetry += 1;
            setReconnectCount(currentRetry);
            if (onReconnect) {
              onReconnect(currentRetry, maxRetries);
            }

            // Exponential backoff delay before retrying (e.g. 1s, 2s, 4s...)
            const backoffMs = retryDelayMs * Math.pow(2, currentRetry - 1);
            await new Promise((resolve) => setTimeout(resolve, backoffMs));

            // Resume stream from current accumulated text length
            return executeStreamAttempt(accumulatedTextRef.current.length);
          }

          // If retries exhausted, attempt graceful degradation to fallback API
          console.warn("[useAIStream] Max retries exhausted. Attempting graceful degradation to sync fallback.");
          try {
            return await fallbackSyncFetch(messagesPayload);
          } catch (fallbackErr: any) {
            const finalError = err instanceof Error ? err : new Error(String(err));
            setError(finalError);
            setIsStreaming(false);
            setIsLoading(false);
            if (onError) {
              onError(finalError, accumulatedTextRef.current);
            }
            throw finalError;
          }
        }
      };

      return executeStreamAttempt(0);
    },
    [
      endpoint,
      defaultHeaders,
      defaultBody,
      maxRetries,
      retryDelayMs,
      autoReconnect,
      onChunk,
      onComplete,
      onError,
      onReconnect,
      fallbackSyncFetch,
    ]
  );

  return {
    completion,
    isLoading,
    isStreaming,
    error,
    reconnectCount,
    isAborted,
    isDegraded,
    metrics,
    startStream,
    abortStream,
    reset,
    fallbackSyncFetch,
  };
}
